import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Slider,
  TextField,
  Button,
  IconButton,
  Paper,
  Tab,
  Tabs,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { Mic, PlayArrow, Pause, Download, Delete, DarkMode, LightMode, Replay10, Forward10 } from '@mui/icons-material';
import { useSpeechRecognition } from 'react-speech-recognition';
import { Howl } from 'howler';
import axios, { AxiosRequestConfig } from 'axios';
import { DownloadedMeditation, AudioPlayerState } from '../types';

const BASE_URL = 'https://api.luminawellnessapp.com';

// Define custom config interface
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  retry?: number;
  retryDelay?: number;
}

// Create axios instance with custom config
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 120000, // 2 minutes timeout for initial request
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add retry logic
api.interceptors.response.use(undefined, async (err) => {
  const { config, message } = err;
  const customConfig = config as CustomAxiosRequestConfig;
  
  if (!customConfig || !customConfig.retry) {
    return Promise.reject(err);
  }
  
  // retry while Network timeout or Network Error
  if (!(message.includes('timeout') || message.includes('Network Error'))) {
    return Promise.reject(err);
  }
  
  customConfig.retry -= 1;
  
  // Create new promise to see which to retry
  const backoff = new Promise((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, customConfig.retryDelay || 1000);
  });
  
  await backoff;
  return api(customConfig);
});

interface MeditationScreenProps {
  isDarkMode: boolean;
  onThemeToggle: () => void;
}

const MeditationScreen: React.FC<MeditationScreenProps> = ({ isDarkMode, onThemeToggle }) => {
  const [duration, setDuration] = useState(5);
  const [loading, setLoading] = useState(false);
  const [audioPath, setAudioPath] = useState<string | null>(null);
  const [playerState, setPlayerState] = useState<AudioPlayerState>({
    isPlaying: false,
    position: 0,
    duration: 0
  });
  const [downloadedMeditations, setDownloadedMeditations] = useState<DownloadedMeditation[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [themeText, setThemeText] = useState('');
  const [sound, setSound] = useState<Howl | null>(null);
  const [voiceGender, setVoiceGender] = useState<'male' | 'female'>('female');
  const [isListening, setIsListening] = useState(false);
  const [userName, setUserName] = useState('');
  const [backgroundMusic, setBackgroundMusic] = useState<'797914' | '102078' | '521533'>('797914');

  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();

  useEffect(() => {
    // Load downloaded meditations from localStorage
    const savedMeditations = localStorage.getItem('downloadedMeditations');
    if (savedMeditations) {
      setDownloadedMeditations(JSON.parse(savedMeditations));
    }
  }, []);

  useEffect(() => {
    // Save downloaded meditations to localStorage when they change
    localStorage.setItem('downloadedMeditations', JSON.stringify(downloadedMeditations));
  }, [downloadedMeditations]);

  // Load user name from localStorage on component mount
  useEffect(() => {
    const savedName = localStorage.getItem('meditationUserName');
    if (savedName) {
      setUserName(savedName);
    }
  }, []);

  // Save user name to localStorage when it changes
  useEffect(() => {
    if (userName) {
      localStorage.setItem('meditationUserName', userName);
    }
  }, [userName]);

  const generateMeditation = async (theme?: string) => {
    if (loading) return;
    setLoading(true);
    setAudioPath(null);
    setPlayerState(prev => ({ ...prev, isPlaying: false, position: 0 }));
    
    // Clean up previous sound if it exists
    if (sound) {
      sound.unload();
    }

    try {
      const config: CustomAxiosRequestConfig = {
        retry: 3,
        retryDelay: 1000
      };

      const response = await api.post('/generate', {
        theme: theme || themeText,
        duration_minutes: duration,
        voice_gender: voiceGender,
        name: userName,
        background_music: backgroundMusic
      }, config);

      if (response.status === 200) {
        const { file } = response.data;
        const fileUrl = `${BASE_URL}/${file}`;
        const audioResponse = await api.get(fileUrl, { 
          responseType: 'blob',
          timeout: 180000 // 3 minutes timeout for audio download
        });
        
        const audioBlob = new Blob([audioResponse.data], { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        setAudioPath(audioUrl);
        
        const newSound = new Howl({
          src: [audioUrl],
          html5: true,
          onplay: () => setPlayerState(prev => ({ ...prev, isPlaying: true })),
          onpause: () => setPlayerState(prev => ({ ...prev, isPlaying: false })),
          onstop: () => setPlayerState(prev => ({ ...prev, isPlaying: false, position: 0 })),
          onend: () => setPlayerState(prev => ({ ...prev, isPlaying: false, position: 0 })),
          onload: () => {
            const duration = newSound.duration();
            setPlayerState(prev => ({ ...prev, duration }));
          },
          onseek: () => {
            const position = newSound.seek();
            setPlayerState(prev => ({ ...prev, position }));
          }
        });

        setSound(newSound);
      }
    } catch (error) {
      console.error('Error generating meditation:', error);
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          alert('The request timed out. Please try again. The server might be busy.');
        } else if (error.response) {
          alert(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          alert('Could not connect to the server. Please check your internet connection.');
        } else {
          alert('An unexpected error occurred. Please try again.');
        }
      } else {
        alert('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = () => {
    if (!sound) return;
    
    if (playerState.isPlaying) {
      sound.pause();
    } else {
      sound.play();
    }
  };

  const handleSeek = (newPosition: number) => {
    if (!sound) return;
    sound.seek(newPosition);
  };

  const handleDownload = async () => {
    if (!audioPath) return;

    try {
      const response = await fetch(audioPath);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `meditation-${Date.now()}.mp3`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Add to downloaded meditations
      const newMeditation: DownloadedMeditation = {
        id: Date.now().toString(),
        title: `Meditation - ${themeText}`,
        filePath: audioPath,
        downloadDate: new Date().toISOString(),
        duration: duration * 60
      };

      setDownloadedMeditations(prev => [...prev, newMeditation]);
    } catch (error) {
      console.error('Error downloading meditation:', error);
    }
  };

  const handleDelete = (id: string) => {
    setDownloadedMeditations(prev => prev.filter(m => m.id !== id));
  };

  // Clean up sound on component unmount
  useEffect(() => {
    return () => {
      if (sound) {
        sound.unload();
      }
    };
  }, [sound]);

  // Update position periodically
  useEffect(() => {
    if (!sound || !playerState.isPlaying) return;

    const interval = setInterval(() => {
      const position = sound.seek();
      setPlayerState(prev => ({ ...prev, position }));
    }, 1000);

    return () => clearInterval(interval);
  }, [sound, playerState.isPlaying]);

  const startListening = () => {
    if (!isListening) {
      setIsListening(true);
      resetTranscript();
      // Start listening
      const recognition = new (window as any).webkitSpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.onresult = (event: any) => {
        const spokenText = event.results[0][0].transcript;
        generateMeditation(spokenText);
      };
      recognition.onend = () => {
        setIsListening(false);
      };
      recognition.start();
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1">
            Lumina, Your Personal Meditation Pal
          </Typography>
          <IconButton onClick={onThemeToggle}>
            {isDarkMode ? <LightMode /> : <DarkMode />}
          </IconButton>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="Generate" />
            <Tab label="Downloads" />
          </Tabs>

          {activeTab === 0 && (
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Your Name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Enter your name or nick name for personalized meditation"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Meditation Theme or Tell Me Your Mood"
                value={themeText}
                onChange={(e) => setThemeText(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                backgroundColor: isListening ? 'rgba(255, 0, 0, 0.1)' : 'transparent',
                padding: '8px',
                borderRadius: '4px',
                transition: 'background-color 0.3s ease'
              }}>
                <IconButton 
                  onClick={startListening}
                  disabled={!browserSupportsSpeechRecognition || isListening}
                >
                  <Mic color={isListening ? 'error' : 'inherit'} />
                </IconButton>
                <Typography sx={{ ml: 1 }}>
                  {!browserSupportsSpeechRecognition 
                    ? 'Speech recognition not supported in this browser'
                    : isListening 
                    ? 'Listening... Speak' 
                    : 'Click to speak'}
                </Typography>
              </Box>

              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: '2fr 3fr',
                gap: 4,
                '& > :last-child': {
                  paddingLeft: 0
                }
              }}>
                <FormControl component="fieldset">
                  <Typography gutterBottom>Voice Gender</Typography>
                  <RadioGroup
                    value={voiceGender}
                    onChange={(e) => setVoiceGender(e.target.value as 'male' | 'female')}
                  >
                    <FormControlLabel value="female" control={<Radio />} label="Female" />
                    <FormControlLabel value="male" control={<Radio />} label="Male" />
                  </RadioGroup>
                </FormControl>

                <FormControl component="fieldset">
                  <Typography gutterBottom>Background Music</Typography>
                  <RadioGroup
                    value={backgroundMusic}
                    onChange={(e) => setBackgroundMusic(e.target.value as '797914' | '102078' | '521533')}
                  >
                    <FormControlLabel value="797914" control={<Radio />} label="Relaxation Music" /> 
                    <FormControlLabel value="102078" control={<Radio />} label="Water Current" />
                    <FormControlLabel value="521533" control={<Radio />} label="Forest" />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Typography gutterBottom>
                Duration: {duration} minutes
              </Typography>
              <Slider
                value={duration}
                onChange={(_, value) => setDuration(value as number)}
                min={1}
                max={10}
                step={1}
                sx={{ mb: 3 }}
              />

              <Button
                variant="contained"
                onClick={() => generateMeditation()}
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : 'Generate Meditation'}
              </Button>

              {audioPath && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, gap: 2 }}>
                    <IconButton onClick={() => handleSeek(Math.max(0, playerState.position - 10))}>
                      <Replay10 />
                    </IconButton>
                    <IconButton onClick={handlePlayPause}>
                      {playerState.isPlaying ? <Pause /> : <PlayArrow />}
                    </IconButton>
                    <IconButton onClick={() => handleSeek(Math.min(playerState.duration, playerState.position + 10))}>
                      <Forward10 />
                    </IconButton>
                    <IconButton onClick={handleDownload}>
                      <Download />
                    </IconButton>
                  </Box>
                  <Slider
                    value={playerState.position}
                    max={playerState.duration}
                    onChange={(_, value) => handleSeek(value as number)}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption">
                      {formatTime(playerState.position)}
                    </Typography>
                    <Typography variant="caption">
                      {formatTime(playerState.duration)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <List sx={{ mt: 3 }}>
              {downloadedMeditations.map((meditation) => (
                <ListItem key={meditation.id}>
                  <ListItemText
                    primary={meditation.title}
                    secondary={`Downloaded: ${new Date(meditation.downloadDate).toLocaleDateString()}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleDelete(meditation.id)}>
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

// Helper function to format time
const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default MeditationScreen; 